class PdpFeatureTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-tab]')];
    this.panels = [...this.querySelectorAll('[data-tab-panel]')];
    this.activeIndex = Math.max(
      0,
      this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );
    this.isVisible = false;
    this.isPaused = false;
    this.interval = null;
    this.abortController = new AbortController();

    if (this.tabs.length === 0 || this.panels.length === 0) return;

    const { signal } = this.abortController;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.activate(index, { focus: false, userInitiated: true }), { signal });
      tab.addEventListener('keydown', (event) => this.handleKeydown(event, index), { signal });
    });

    this.addEventListener('pointerenter', this.pause, { signal });
    this.addEventListener('pointerleave', this.resume, { signal });
    this.addEventListener('focusin', this.pause, { signal });
    this.addEventListener('focusout', this.handleFocusOut, { signal });
    document.addEventListener('visibilitychange', this.handleDocumentVisibility, { signal });

    this.visibilityObserver = new IntersectionObserver(this.handleVisibility, { threshold: 0.2 });
    this.visibilityObserver.observe(this);

    this.activate(this.activeIndex, { focus: false, userInitiated: false });
    this.startRotation();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.visibilityObserver?.disconnect();
    this.stopRotation();
  }

  handleKeydown(event, index) {
    let targetIndex = null;

    if (event.key === 'ArrowLeft') targetIndex = (index - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'ArrowRight') targetIndex = (index + 1) % this.tabs.length;
    if (event.key === 'Home') targetIndex = 0;
    if (event.key === 'End') targetIndex = this.tabs.length - 1;

    if (targetIndex === null) return;

    event.preventDefault();
    this.activate(targetIndex, { focus: true, userInitiated: true });
  }

  handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.resume();
  };

  handleDocumentVisibility = () => {
    if (document.hidden) {
      this.pause();
      this.pauseAllMedia();
    } else {
      this.resume();
      this.playActiveMedia();
    }
  };

  handleVisibility = ([entry]) => {
    this.isVisible = entry.isIntersecting;

    if (this.isVisible) {
      this.playActiveMedia();
      this.startRotation();
    } else {
      this.pauseAllMedia();
      this.stopRotation();
    }
  };

  activate(index, { focus = false, userInitiated = false } = {}) {
    const targetIndex = Math.max(0, Math.min(index, this.tabs.length - 1));
    this.activeIndex = targetIndex;

    this.tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === targetIndex;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === targetIndex;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
    });

    this.loadMedia(targetIndex);
    this.pauseAllMedia(targetIndex);
    this.playActiveMedia();

    const activeTab = this.tabs[targetIndex];
    this.centerTab(activeTab);
    if (focus) activeTab?.focus();

    if (userInitiated) {
      this.stopRotation();
      this.startRotation();
    }
  }

  loadMedia(index) {
    const video = this.getVideo(index);
    if (!video || video.dataset.loaded === 'true') return;

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
    });

    video.dataset.loaded = 'true';
    video.load();
  }

  playActiveMedia() {
    if (!this.isVisible || !this.hasAttribute('autoplay-media')) return;

    const video = this.getVideo(this.activeIndex);
    video?.play().catch(() => {});
  }

  pauseAllMedia(exceptIndex = -1) {
    this.panels.forEach((_panel, index) => {
      if (index === exceptIndex) return;
      this.getVideo(index)?.pause();
    });
  }

  pause = () => {
    this.isPaused = true;
    this.stopRotation();
  };

  resume = () => {
    this.isPaused = false;
    this.startRotation();
  };

  startRotation() {
    if (
      this.interval ||
      this.isPaused ||
      !this.isVisible ||
      document.hidden ||
      !this.hasAttribute('auto-rotate') ||
      this.tabs.length < 2 ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    const interval = Number(this.getAttribute('rotation-interval')) || 6000;

    this.interval = window.setInterval(() => {
      const nextIndex = (this.activeIndex + 1) % this.tabs.length;
      this.activate(nextIndex, { focus: false, userInitiated: false });
    }, interval);
  }

  stopRotation() {
    window.clearInterval(this.interval);
    this.interval = null;
  }

  centerTab(tab) {
    if (!tab) return;

    const scroller = tab.closest('.pdp-feature__tabs-scroll');
    if (!scroller) return;

    const left = tab.offsetLeft - (scroller.clientWidth - tab.offsetWidth) / 2;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scroller.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  getVideo(index) {
    return this.panels[index]?.querySelector('[data-feature-video]') ?? null;
  }
}

if (!customElements.get('pdp-feature-tabs')) {
  customElements.define('pdp-feature-tabs', PdpFeatureTabs);
}
